import { CitySelect, CountrySelect, StateSelect } from '@/views/screens/common';
import { Col, Form, type FormInstance, Row } from 'antd';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getPostalSearchProgress } from '../../../../redux/common/CommonActions';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import FormInput from '../../../components/Inputs/FormInput';
import { useBehaviorForAddressFields } from '../CustomHooks/SearchQuoteHooks';

export interface PostalSearchFormValues {
  countryCode: string;
  stateCode?: string;
  city?: string;
  postalCode?: string;
}

interface PostalSearchFormProps {
  form: FormInstance<PostalSearchFormValues>;
  onSubmit: () => void;
  labelForTestId?: string;
}

const PostalSearchForm: FC<PostalSearchFormProps> = ({ form, onSubmit, labelForTestId }) => {
  const dispatch = useDispatch();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const countryCode = Form.useWatch('countryCode', form);
  const stateCode = Form.useWatch('stateCode', form);

  const handleSubmit = (data: PostalSearchFormValues) => {
    const payload = {
      CountryCode: data?.countryCode,
      StateCode: data?.stateCode,
      City: data?.city,
      PostalCode: data?.postalCode,
      PageIndex: 1,
      PageSize: 10,
    };
    dispatch(getPostalSearchProgress(payload));
    if (onSubmit) {
      onSubmit();
    }
  };

  useBehaviorForAddressFields(countryCode, stateCode, 'stateCode', 'city', form);

  return (
    <Form<PostalSearchFormValues> form={form} onFinish={handleSubmit}>
      <Row gutter={15}>
        <Col md={6}>
          <CountrySelect
            name="countryCode"
            rules={[
              {
                required: true,
                message: t('translation:default.fields.country.message'),
              },
            ]}
          />
        </Col>
        <Col md={6}>
          <StateSelect name="stateCode" countryCode={countryCode} />
        </Col>
        <Col md={6}>
          <CitySelect name="city" countryCode={countryCode} stateCode={stateCode} />
        </Col>
        <Col md={6}>
          <FormInput
            name="postalCode"
            label={t('translation:default.fields.postalCode.label')}
            placeholder={t('translation:default.fields.postalCode.placeholder')}
            editMode
            maxLength={10}
          />
        </Col>
      </Row>
      <Row justify="end" gutter={15}>
        <Col>
          <Button buttonType="submit" data-testid={`${labelForTestId}-postal-modal-search`}>
            {t('translation:default.buttons.search')}
          </Button>
        </Col>
        <Col>
          <Button
            onClick={() => form.resetFields()}
            type="default"
            data-testid={`${labelForTestId}-postal-modal-clear`}
          >
            {t('translation:default.buttons.clear')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default PostalSearchForm;
